import { useEffect, useState } from "react";
import api, { API, formatCLP, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import {
  CurrencyDollar, Users, GraduationCap, Certificate, Star, Plus, Pencil, Trash,
  Eye, EyeSlash, DownloadSimple, PaperPlaneTilt, Ticket, FloppyDisk, FilmSlate, Info,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const empty = { title: "", description: "", category: "General", modality: "online", duration_hours: 10, price: 50000, image_url: "", passing_grade: 4.0, min_attendance: 75, published: true, objectives: [], syllabus: [] };

export default function AdminPanel() {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [certs, setCerts] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [surveyCfg, setSurveyCfg] = useState({ questions: [], active: true });
  const [courseForm, setCourseForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [courseOpen, setCourseOpen] = useState(false);
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_pct: 10 });

  const load = () => {
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/courses?all_states=true").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/admin/certificates").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/admin/surveys").then((r) => setSurveys(r.data)).catch(() => {});
    api.get("/admin/instructors").then((r) => setInstructors(r.data)).catch(() => {});
    api.get("/admin/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/admin/survey-config").then((r) => setSurveyCfg(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openNew = () => { setCourseForm(empty); setEditId(null); setCourseOpen(true); };
  const openEdit = (c) => {
    setCourseForm({ ...c, objectives: c.objectives || [], syllabus: c.syllabus || [] });
    setEditId(c.id); setCourseOpen(true);
  };
  const saveCourse = async (e) => {
    e.preventDefault();
    const payload = {
      ...courseForm,
      duration_hours: parseInt(courseForm.duration_hours),
      price: parseFloat(courseForm.price),
      passing_grade: parseFloat(courseForm.passing_grade),
      min_attendance: parseFloat(courseForm.min_attendance),
      objectives: typeof courseForm.objectives === "string" ? courseForm.objectives.split("\n").filter(Boolean) : courseForm.objectives,
      syllabus: typeof courseForm.syllabus === "string" ? courseForm.syllabus.split("\n").filter(Boolean) : courseForm.syllabus,
      modules: courseForm.modules || [], quiz: courseForm.quiz || [],
    };
    try {
      if (editId) await api.put(`/courses/${editId}`, payload);
      else await api.post("/courses", payload);
      toast.success(editId ? "Curso actualizado" : "Curso creado"); setCourseOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const togglePub = async (c) => { await api.patch(`/courses/${c.id}/publish`); load(); };
  const delCourse = async (c) => { if (window.confirm(`¿Eliminar "${c.title}"?`)) { await api.delete(`/courses/${c.id}`); toast.success("Eliminado"); load(); } };
  const updateRole = async (uid, role) => { await api.patch(`/admin/users/${uid}/role`, { role }); toast.success("Rol actualizado"); load(); };
  const addCoupon = async (e) => { e.preventDefault(); try { await api.post("/admin/coupons", { code: newCoupon.code, discount_pct: parseFloat(newCoupon.discount_pct), active: true }); toast.success("Cupón creado"); setNewCoupon({ code: "", discount_pct: 10 }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const resend = async (folio) => { try { await api.post(`/admin/certificates/${folio}/resend`); toast.success("Certificado reenviado por correo"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const setCompanyDiscount = async (cid, pct) => { try { await api.patch(`/admin/companies/${cid}`, { discount_pct: parseFloat(pct) || 0 }); toast.success("Descuento de convenio actualizado"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const addSq = () => setSurveyCfg((s) => ({ ...s, questions: [...s.questions, { id: "q_" + Date.now(), text: "", type: "rating", options: [] }] }));
  const updSq = (i, patch) => setSurveyCfg((s) => ({ ...s, questions: s.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q) }));
  const delSq = (i) => setSurveyCfg((s) => ({ ...s, questions: s.questions.filter((_, idx) => idx !== i) }));
  const saveSurveyCfg = async () => {
    try {
      await api.put("/admin/survey-config", {
        active: true,
        questions: surveyCfg.questions.filter((q) => q.text.trim()).map((q) => ({
          ...q, options: typeof q.options === "string" ? q.options.split("\n").filter(Boolean) : (q.options || []),
        })),
      });
      toast.success("Encuesta actualizada"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const kpis = [
    { label: "Ventas totales", value: formatCLP(reports?.total_sales), icon: CurrencyDollar },
    { label: "Alumnos activos", value: reports?.active_students ?? 0, icon: Users },
    { label: "Tasa aprobación", value: `${reports?.approval_rate ?? 0}%`, icon: GraduationCap },
    { label: "Certificados", value: reports?.certificates_issued ?? 0, icon: Certificate },
    { label: "Satisfacción", value: `${reports?.avg_satisfaction ?? 0}★`, icon: Star },
  ];
  const roleLabels = { student: "Alumno", company: "Empresa", instructor: "Relator", admin: "Admin" };
  const fmt = (d) => new Date(d).toLocaleDateString("es-CL");

  return (
    <div>
      <p className="text-xs uppercase tracking-overline text-primary mb-1 font-mono">Administración</p>
      <h1 className="font-display text-3xl font-800 tracking-tight mb-8">Panel de control</h1>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {kpis.map((k) => { const Icon = k.icon; return (
          <div key={k.label} className="bg-white border border-border rounded-lg p-5">
            <Icon size={22} className="text-primary mb-3" weight="duotone" />
            <div className="font-display text-xl font-800 tracking-tight">{k.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
          </div>
        );})}
      </div>

      <Tabs defaultValue="reports">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Reportes</TabsTrigger>
          <TabsTrigger value="courses" data-testid="admin-tab-courses">Cursos</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Usuarios</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Cupones</TabsTrigger>
          <TabsTrigger value="companies" data-testid="admin-tab-companies">Empresas</TabsTrigger>
          <TabsTrigger value="certs" data-testid="admin-tab-certs">Certificados</TabsTrigger>
          <TabsTrigger value="surveys" data-testid="admin-tab-surveys">Encuestas</TabsTrigger>
          <TabsTrigger value="surveycfg" data-testid="admin-tab-surveycfg">Config. encuesta</TabsTrigger>
          <TabsTrigger value="logs" data-testid="admin-tab-logs">Actividad</TabsTrigger>
        </TabsList>

        {/* Reports */}
        <TabsContent value="reports" className="mt-4">
          <div className="bg-white border border-border rounded-lg p-6">
            <h3 className="font-display font-800 mb-4">Cursos más vendidos</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={reports?.top_courses || []}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
                <Tooltip formatter={(v) => formatCLP(v)} />
                <Bar dataKey="sales" fill="#0066FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        {/* Courses CRUD */}
        <TabsContent value="courses" className="mt-4">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4 flex gap-3 text-sm text-slate-700">
            <Info size={20} className="text-primary shrink-0 mt-0.5" />
            <div>
              <b>¿Cómo publicar un curso online?</b> Crea el curso con <b>"Nuevo curso"</b>, agrega módulos y lecciones, y en cada lección de tipo <b>Video</b> sube el archivo (o pega una URL .mp4). También puedes adjuntar PDFs/guías. Al terminar, activa <b>"Publicado"</b> para que aparezca en el catálogo. Usa <b>Contenido</b> en cada fila para editar videos y lecciones.
            </div>
          </div>
          <div className="flex justify-end mb-4">
            <Button onClick={() => navigate("/dashboard/curso-editor/new")} className="bg-primary text-white hover:bg-blue-600 gap-2" data-testid="admin-new-course"><Plus size={18} /> Nuevo curso</Button>
          </div>
          <div className="bg-white border border-border rounded-lg overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Curso</TableHead><TableHead>Categoría</TableHead><TableHead>Precio</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
              <TableBody>
                {courses.map((c) => (
                  <TableRow key={c.id} data-testid={`admin-course-${c.id}`}>
                    <TableCell className="font-medium max-w-[200px] truncate">{c.title}</TableCell>
                    <TableCell>{c.category}</TableCell>
                    <TableCell>{formatCLP(c.price)}</TableCell>
                    <TableCell>{c.published ? <Badge className="bg-primary text-white">Publicado</Badge> : <Badge variant="outline">Borrador</Badge>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/curso-editor/${c.id}`)} className="gap-1 mr-1" data-testid={`content-course-${c.id}`}><FilmSlate size={15} /> Contenido</Button>
                      <Button size="icon" variant="ghost" onClick={() => togglePub(c)} title="Publicar/Despublicar" data-testid={`toggle-pub-${c.id}`}>{c.published ? <EyeSlash size={16} /> : <Eye size={16} />}</Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)} title="Editar datos" data-testid={`edit-course-${c.id}`}><Pencil size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => delCourse(c)} className="text-destructive" data-testid={`del-course-${c.id}`}><Trash size={16} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-4">
          <div className="bg-white border border-border rounded-lg overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Correo</TableHead><TableHead>RUT</TableHead><TableHead>Rol</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.rut || "—"}</TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                        <SelectTrigger className="w-32 h-8" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {["student", "company", "instructor", "admin"].map((r) => <SelectItem key={r} value={r}>{roleLabels[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Coupons */}
        <TabsContent value="coupons" className="mt-4">
          <form onSubmit={addCoupon} className="flex flex-wrap gap-3 items-end mb-4 bg-white border border-border rounded-lg p-4">
            <div><Label>Código</Label><Input value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} required data-testid="coupon-code" className="mt-1" placeholder="VERANO25" /></div>
            <div><Label>% Descuento</Label><Input type="number" value={newCoupon.discount_pct} onChange={(e) => setNewCoupon({ ...newCoupon, discount_pct: e.target.value })} required data-testid="coupon-pct" className="mt-1 w-28" /></div>
            <Button type="submit" className="bg-primary text-white hover:bg-blue-600 gap-2" data-testid="coupon-add"><Ticket size={18} /> Crear cupón</Button>
          </form>
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Código</TableHead><TableHead>Descuento</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
              <TableBody>{coupons.map((c) => <TableRow key={c.id}><TableCell className="font-mono font-medium">{c.code}</TableCell><TableCell>{c.discount_pct}%</TableCell><TableCell>{c.active ? <Badge className="bg-primary text-white">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</TableCell></TableRow>)}</TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Companies */}
        <TabsContent value="companies" className="mt-4">
          <div className="bg-white border border-border rounded-lg overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Empresa</TableHead><TableHead>RUT</TableHead><TableHead>Trabajadores</TableHead><TableHead>Cupos</TableHead><TableHead>Facturado</TableHead><TableHead>Dcto. convenio %</TableHead></TableRow></TableHeader>
              <TableBody>
                {companies.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin empresas registradas.</TableCell></TableRow>
                  : companies.map((c) => (
                    <TableRow key={c.id} data-testid={`admin-company-${c.id}`}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.rut || "—"}</TableCell>
                      <TableCell>{c.workers}</TableCell>
                      <TableCell>{c.used_seats}/{c.seats}</TableCell>
                      <TableCell>{formatCLP(c.total_spent)}</TableCell>
                      <TableCell>
                        <Input type="number" defaultValue={c.discount_pct} className="w-24 h-8" data-testid={`company-discount-${c.id}`}
                          onBlur={(e) => e.target.value != c.discount_pct && setCompanyDiscount(c.id, e.target.value)} />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">El descuento de convenio se aplica a las compras masivas de cupos de la empresa. Presiona fuera del campo para guardar.</p>
        </TabsContent>

        {/* Certificates */}
        <TabsContent value="certs" className="mt-4">
          <div className="bg-white border border-border rounded-lg overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Alumno</TableHead><TableHead>Curso</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
              <TableBody>
                {certs.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sin certificados aún.</TableCell></TableRow>
                  : certs.map((c) => (
                    <TableRow key={c.folio} data-testid={`admin-cert-${c.folio}`}>
                      <TableCell className="font-mono text-xs">{c.folio}</TableCell>
                      <TableCell className="font-medium">{c.user_name}</TableCell>
                      <TableCell className="max-w-[180px] truncate">{c.course_name}</TableCell>
                      <TableCell>{fmt(c.issued_at)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <a href={`${API}/certificates/${c.folio}/download`} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost"><DownloadSimple size={16} /></Button></a>
                        <Button size="icon" variant="ghost" onClick={() => resend(c.folio)} title="Reenviar" data-testid={`resend-${c.folio}`}><PaperPlaneTilt size={16} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Surveys */}
        <TabsContent value="surveys" className="mt-4">
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Alumno</TableHead><TableHead>Curso</TableHead><TableHead>Valoración</TableHead><TableHead>Comentario</TableHead></TableRow></TableHeader>
              <TableBody>
                {surveys.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Sin respuestas aún.</TableCell></TableRow>
                  : surveys.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.user_name}</TableCell>
                      <TableCell>{s.course_name}</TableCell>
                      <TableCell className="flex items-center gap-1">{s.rating} <Star size={14} weight="fill" className="text-accent" /></TableCell>
                      <TableCell className="text-muted-foreground max-w-[240px] truncate">{s.comment || "—"}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Survey config */}
        <TabsContent value="surveycfg" className="mt-4">
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
            <p className="text-sm text-muted-foreground">Define las preguntas que verá el alumno al finalizar un curso.</p>
            <div className="flex gap-2">
              <Button onClick={addSq} variant="outline" size="sm" className="gap-1" data-testid="sc-add"><Plus size={16} /> Pregunta</Button>
              <Button onClick={saveSurveyCfg} className="bg-primary text-white hover:bg-blue-600 gap-1" data-testid="sc-save"><FloppyDisk size={16} /> Guardar</Button>
            </div>
          </div>
          <div className="space-y-3">
            {surveyCfg.questions.map((q, i) => (
              <div key={q.id} className="bg-white border border-border rounded-lg p-4" data-testid={`sc-q-${i}`}>
                <div className="flex flex-wrap gap-2 items-center mb-2">
                  <span className="font-semibold text-sm">{i + 1}.</span>
                  <Input value={q.text} onChange={(e) => updSq(i, { text: e.target.value })} placeholder="Texto de la pregunta" className="flex-1 min-w-[200px]" data-testid={`sc-text-${i}`} />
                  <Select value={q.type} onValueChange={(v) => updSq(i, { type: v })}>
                    <SelectTrigger className="w-48" data-testid={`sc-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="rating">Calificación (estrellas)</SelectItem>
                      <SelectItem value="choice">Selección</SelectItem>
                      <SelectItem value="text">Texto libre</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => delSq(i)} size="icon" variant="ghost" className="text-destructive"><Trash size={15} /></Button>
                </div>
                {q.type === "choice" && (
                  <Textarea value={Array.isArray(q.options) ? q.options.join("\n") : q.options}
                    onChange={(e) => updSq(i, { options: e.target.value.split("\n") })}
                    placeholder="Opciones (una por línea)" className="text-sm" data-testid={`sc-options-${i}`} />
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Activity logs (SENCE) */}
        <TabsContent value="logs" className="mt-4">
          <div className="bg-white border border-border rounded-lg p-6">
            <h3 className="font-display font-800 mb-2">Registro de actividad (control de asistencia)</h3>
            <p className="text-sm text-muted-foreground mb-4">Logs de conexión, tiempo y % de avance. Exportables para reporte externo (SENCE).</p>
            <div className="flex flex-wrap gap-3">
              <a href={`${API}/admin/sence-report?fmt=csv`} target="_blank" rel="noreferrer"><Button className="bg-primary text-white hover:bg-blue-600 gap-2" data-testid="sence-csv"><DownloadSimple size={18} /> Reporte SENCE (CSV)</Button></a>
              <a href={`${API}/admin/sence-report?fmt=xlsx`} target="_blank" rel="noreferrer"><Button className="bg-primary text-white hover:bg-blue-600 gap-2" data-testid="sence-xlsx"><DownloadSimple size={18} /> Reporte SENCE (Excel)</Button></a>
              <a href={`${API}/admin/activity-logs/export?fmt=csv`} target="_blank" rel="noreferrer"><Button variant="outline" className="gap-2" data-testid="export-csv"><DownloadSimple size={18} /> Logs crudos CSV</Button></a>
              <a href={`${API}/admin/activity-logs/export?fmt=xlsx`} target="_blank" rel="noreferrer"><Button variant="outline" className="gap-2" data-testid="export-xlsx"><DownloadSimple size={18} /> Logs crudos Excel</Button></a>
            </div>
            <p className="text-xs text-muted-foreground mt-3">El <b>Reporte SENCE</b> entrega el registro de asistencia e-learning (código curso, RUT, nombre, conexiones, tiempo conectado, % avance/asistencia, nota y estado) en formato compatible con LCE.</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Course dialog */}
      <Dialog open={courseOpen} onOpenChange={setCourseOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display font-800">{editId ? "Editar curso" : "Nuevo curso"}</DialogTitle></DialogHeader>
          <form onSubmit={saveCourse} className="space-y-3">
            <div><Label>Título</Label><Input value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} required data-testid="cf-title" className="mt-1" /></div>
            <div><Label>Descripción</Label><Textarea value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} data-testid="cf-desc" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categoría</Label><Input value={courseForm.category} onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })} data-testid="cf-cat" className="mt-1" /></div>
              <div><Label>Modalidad</Label>
                <Select value={courseForm.modality} onValueChange={(v) => setCourseForm({ ...courseForm, modality: v })}>
                  <SelectTrigger className="mt-1" data-testid="cf-modality"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="online">Online</SelectItem><SelectItem value="presencial">Presencial</SelectItem><SelectItem value="mixta">Mixta</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Horas</Label><Input type="number" value={courseForm.duration_hours} onChange={(e) => setCourseForm({ ...courseForm, duration_hours: e.target.value })} data-testid="cf-hours" className="mt-1" /></div>
              <div><Label>Precio</Label><Input type="number" value={courseForm.price} onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })} data-testid="cf-price" className="mt-1" /></div>
              <div><Label>Relator</Label>
                <Select value={courseForm.instructor_id || ""} onValueChange={(v) => setCourseForm({ ...courseForm, instructor_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="cf-instructor"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-white">{instructors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nota mínima</Label><Input type="number" step="0.1" value={courseForm.passing_grade} onChange={(e) => setCourseForm({ ...courseForm, passing_grade: e.target.value })} data-testid="cf-grade" className="mt-1" /></div>
              <div><Label>Asistencia mín. (%)</Label><Input type="number" value={courseForm.min_attendance} onChange={(e) => setCourseForm({ ...courseForm, min_attendance: e.target.value })} data-testid="cf-att" className="mt-1" /></div>
            </div>
            <div><Label>URL imagen</Label><Input value={courseForm.image_url} onChange={(e) => setCourseForm({ ...courseForm, image_url: e.target.value })} data-testid="cf-img" className="mt-1" placeholder="https://..." /></div>
            <div><Label>Objetivos (uno por línea)</Label><Textarea value={Array.isArray(courseForm.objectives) ? courseForm.objectives.join("\n") : courseForm.objectives} onChange={(e) => setCourseForm({ ...courseForm, objectives: e.target.value })} className="mt-1" /></div>
            <DialogFooter><Button type="submit" className="bg-primary text-white hover:bg-blue-600" data-testid="cf-save">{editId ? "Guardar cambios" : "Crear curso"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
